/**
 * dealMessaging.ts — both message composers, against the real conversation
 * endpoints.
 *
 * Two sends, and they are not symmetrical:
 *
 *   #msg-send      the deal thread's composer. Target is the person whose thread
 *                  is in the pane: `dealsSel` when it is that person's deal, else
 *                  the painted row's thread (resolve below).
 *   #msgrail-send  the message side rail, which opens over any route. Target is
 *                  `msgRailFor`, a HANDLE rather than a thread.
 *
 * 🪤 THE ENTER KEYS DIFFER, AND ONLY ONE IS REACHABLE FROM A CLICK LISTENER.
 * `#msg-in`'s Enter does `$("#msg-send").click()`, and a synthetic click
 * dispatches through the full capture path, so the click handler below sees it.
 * `#msgrail-in`'s Enter calls `railSend()` DIRECTLY — no click is ever
 * dispatched, so no click-phase handler can see it, and that composer needs its
 * own keydown listener. Same split writeGuard already documents in
 * GUARDED_ENTER_INPUTS, verified the same way, and the same shape commentPost
 * hit with #cm-in vs [data-takefor].
 *
 * 🔑 A STAGED PIECE GOES AS THE APP'S OWN SHARED-LISTING MESSAGE. Both
 * composers let a visitor stage pieces from their collection (the share sheet,
 * app.js), and Blake paints them as cards. The app has a wire format for
 * exactly this, and it is plain text through the same message route: a body
 * that is EXACTLY "[Shared listing: <listingId>]", one listing per message,
 * nothing else in that body (the app's renderers match it anchored at both
 * ends: chat/[userId].tsx, deal-thread/[key].tsx; sent by both and by
 * ShareListingModal.tsx). A typed note goes as its OWN message AFTER the
 * listing landed, so it can never arrive referring to a card the recipient
 * cannot see (ShareListingModal.tsx). Each message carries its OWN
 * Idempotency-Key: the server keys replays per user and key, so a key shared
 * across the listing and the note would replay the listing and drop the note.
 * Before this the send was refused with "Item links can't be sent from the web
 * yet"; that was the right refusal for a format nobody had checked existed,
 * and wrong once it was found.
 *
 * 🪤 BLAKE'S OWN threadFor() INVENTS A ROW for an unknown handle, keyed
 * `t-<handle>` (data.js). That id is local fiction and must never reach the
 * wire, which is why resolution goes handle -> installed thread key -> the
 * handle→id map, and refuses out loud rather than falling through to it.
 *
 * Both selectors stay in GUARDED_WRITES: his handlers push a bubble into a LIVE
 * conversation and the rail even patches it into the DOM without a re-render, so
 * letting them run alongside a real POST would show a message that may not have
 * been accepted. The guard kills them; this does the write; if this ever fails
 * to install the composers go back to dead rather than to lying.
 */

import { installDealThread, refreshDeals, threadWrites, SHARED_LISTING_FALLBACK } from "./floorTransport";
import { newAttemptId } from "./makeOffer";

export const MSG_SEND_SELECTOR = "#msg-send";
export const RAIL_SEND_SELECTOR = "#msgrail-send";
export const MSG_INPUT_SELECTOR = "#msg-in";
export const RAIL_INPUT_SELECTOR = "#msgrail-in";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't send that message — please try again";
export const SIGNIN_MESSAGE = "Sign in to send a message";
/** The counterpart resolved to no id — see the header. Never a silent no-op. */
export const UNKNOWN_RECIPIENT_MESSAGE =
  "Couldn't work out who this thread is with — open their profile and message from there";
/** Mobile's own words (ShareListingModal.tsx) when every listing landed and the
 *  note did not: the recipient can see the card; the note is back in the box. */
export const NOTE_NOT_SENT_MESSAGE = "Listing sent, note was not";
/** Said only when the message went but the thread could not be re-read. */
export const SENT_MESSAGE = "Sent";
/** The bubble's timestamp slot while the message is on the wire, and after it
 *  landed but before the thread has been re-read to give it a real one. */
export const SENDING_LABEL = "Sending…";
export const JUST_SENT_LABEL = "Just now";

/** The app's shared-listing body, built here and nowhere else on the web. The
 *  whole body: the prefix, the listing id, the closing bracket, nothing more. */
export const SHARED_LISTING_PREFIX = "[Shared listing: ";
export function sharedListingBody(listingId: string): string {
  return `${SHARED_LISTING_PREFIX}${listingId}]`;
}

type Composer = "deal" | "rail";

/** A staged piece, as the bridge answers it (app.js msgAttach, tagged with the person it was staged for). */
interface StagedItem {
  lid: string;
  title: string;
  image: string;
  value: number | null;
}

interface MessagingWindow extends Window {
  toast?: (message: string) => void;
  render?: () => void;
  rerenderKeepScroll?: () => void;
  __gbzDealsSel?: () => string | null;
  __gbzMsgRailFor?: () => string | null;
  __gbzMsgAttachCount?: () => number;
  __gbzMsgAttachFor?: (handle: string) => StagedItem[];
  __gbzMsgAttachDrop?: (handle: string, lids: string[] | null) => number;
  __gbzThreadHandle?: (threadId: string) => string | null;
  __gbzThreadIdForHandle?: (handle: string) => string | null;
  __gbzThreadConvoId?: (threadId: string) => string | null;
  __gbzThreadMsgs?: (threadId: string) => PendingAwareMsg[] | null;
  __gbzDealsJustSent?: () => boolean;
}

/** One entry of a thread's msgs, as Blake's renderer reads it. `pending` is
 *  ours: it marks the bubble that stands in for a message still on the wire.
 *  `item` is the card a shared listing paints (msgBubbleHTML draws the card and
 *  not the text when it is set; the row line reads the text). */
interface PendingAwareMsg {
  from: string;
  text: string;
  when: string;
  at: string;
  pending?: true;
  item?: StagedItem;
}

/** One body on its way out: a staged piece's token, or the note. */
interface Outgoing {
  body: string;
  key: string;
  item: StagedItem | null;
}

/**
 * The counterpart's user id, out of a thread key.
 *
 * `d:<counterpartId>:<listingId>` and `p:<counterpartId>` both carry it; anything
 * else — including Blake's invented `t-<handle>` — returns null so the caller
 * refuses instead of sending an id the server has never seen.
 */
export function counterpartIdFromThreadKey(threadId: string | null): string | null {
  if (!threadId) return null;
  if (threadId.startsWith("d:")) return threadId.split(":")[1] || null;
  if (threadId.startsWith("p:")) return threadId.slice(2) || null;
  return null;
}

/**
 * The thread renderDeals actually has open, read off the DOM.
 *
 * Mirrors his own selection rule (`list.find(id === dealsSel) || list[0]`) for
 * the case where dealsSel is null but a thread is painted, and it is the same
 * read installDealThreadLoader makes for the same reason.
 */
export function openThreadIdFromDom(doc: Document = document): string | null {
  const open = doc.querySelector(".thread-row.on, .thread-view [data-threadcta]");
  const row = open?.closest?.("[data-selthread]") as HTMLElement | null;
  if (row?.dataset.selthread) return row.dataset.selthread;
  const first = doc.querySelector("[data-selthread]") as HTMLElement | null;
  return first?.dataset.selthread ?? null;
}

export function installDealMessaging(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  myUserId: string,
  /** handle -> users.id, the same live object installFollowUser holds. Held by
   *  reference, so entries added after boot are visible here too. */
  userIds: Record<string, string>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as MessagingWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* In flight, keyed by RECIPIENT rather than by composer, and that is the
     load-bearing choice. His handlers were synchronous; with two awaits behind
     the button an Enter held down is several copies of the same message.

     🔴 AND BOTH COMPOSERS CAN BE ON SCREEN AT ONCE, aimed at the same person.
     POST /api/conversations is get-or-create with NO unique index behind it —
     conversations has three plain indexes and none on the participant pair — so
     two concurrent creates for one pair can both miss the SELECT and both
     insert. A duplicate row silently splits the thread in half, and every
     reader takes .limit(1). Keying this by composer would have left that door
     open; keying it by recipient closes it. */
  const busy = new Set<string>();

  function repaint(): void {
    const w = win as MessagingWindow;
    /* rerenderKeepScroll, not render: the rail can be open over any route and
       the deal thread sits mid-page. Both are top-level function declarations in
       a classic script, so they are real window properties. */
    if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
    else w.render?.();
  }

  /** Which composer, its input, and the thread/handle it is aimed at. */
  function resolve(kind: Composer): {
    input: HTMLInputElement;
    threadId: string | null;
    recipientId: string | null;
    /** The person the composer is aimed at, as a handle: the rail's msgRailFor,
     *  or the pane's person read off the installed row. Staged pieces are
     *  tagged with it (the share sheet's person), so it decides which pieces
     *  this composer sends. Null when unknown; nothing is staged for null. */
    handle?: string | null;
  } | null {
    const w = win as MessagingWindow;
    const input = win.document.querySelector(
      kind === "deal" ? MSG_INPUT_SELECTOR : RAIL_INPUT_SELECTOR,
    ) as HTMLInputElement | null;
    if (!input) return null;

    if (kind === "deal") {
      /* 🪤 dealsSel IS NULL ON FIRST ARRIVAL, WITH A THREAD VISIBLY OPEN. Our own
         boot nulls it (installFloor), while renderDeals still paints one anyway:
         `const sel = list.find((t) => t.id === dealsSel) || list[0]`. So reading
         the binding alone would refuse out loud on the exact screen a visitor
         lands on — and his own handler would have thrown there, which is masked
         today only because the guard stops it first.
         The DOM read mirrors his `|| list[0]` and is the same read
         installDealThreadLoader already makes to find the open thread.
         🔑 THE PERSON IN THE PANE (card 1219285769469932). dealsSel when it is
         a deal of the painted row's person, else the painted row's thread. A
         selection that is off the tab being shown (on a computer, Account >
         Orders keeps one from another tab) leaves the pane on the tab's first
         person, and reading dealsSel alone sent a message typed there to the
         hidden selection. For the same person dealsSel is kept, not the row's
         id: the row names their LATEST deal, and a list read can bring that as
         a new row with an empty messages array and no conversation id yet, so
         aiming at it put the Sending line where the pane does not paint it and
         sent the message through find-or-create. The last `?? dealsSel` is for a
         pane with no rows, which paints no composer today (#msg-in is only in
         renderPersonThread, under a painted list), so it is unreachable. */
      const row = openThreadIdFromDom(win.document);
      const sel = w.__gbzDealsSel?.() ?? null;
      const selWho = counterpartIdFromThreadKey(sel);
      const threadId = selWho !== null && selWho === counterpartIdFromThreadKey(row) ? sel : row ?? sel;
      const handle = threadId ? w.__gbzThreadHandle?.(threadId) ?? null : null;
      return { input, threadId, recipientId: counterpartIdFromThreadKey(threadId), handle };
    }
    const handle = w.__gbzMsgRailFor?.() ?? null;
    if (!handle) return { input, threadId: null, recipientId: null };
    const threadId = w.__gbzThreadIdForHandle?.(handle) ?? null;
    // The installed thread's key first, the shared map second — the same
    // precedence followUser uses, and for the same reason: a value the server
    // gave us beats a value we assembled.
    return { input, threadId, recipientId: counterpartIdFromThreadKey(threadId) ?? userIds[handle] ?? null, handle };
  }

  /* The network half, in two steps so several bodies can share one
     conversation lookup and still each carry their own key. Neither step
     touches the screen or the toast: everything the visitor sees change lives
     in submit(), which is the only caller and decides what to say. */
  type Outcome = { ok: true } | { ok: false; reason: string };

  /** The pair's conversation id by find-or-create, for a thread whose id is
   *  not known yet (submit reads the known one itself, so that with it known
   *  the first message is on the wire before submit yields). */
  async function createConversation(
    recipientId: string,
  ): Promise<{ ok: true; id: string } | { ok: false; reason: string }> {
    /* Get-or-create. It answers the existing row for a pair that has spoken
       before and only applies the messaging-privacy gate to a NEW conversation,
       which is the server's own ruling: an existing thread keeps working when
       the recipient later tightens the setting, so that dispute history is not
       severed. */
    const convoRes = await authFetch("/api/conversations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recipientId }),
    });
    if (convoRes.status === 401) return { ok: false, reason: SIGNIN_MESSAGE };
    if (!convoRes.ok) {
      /* The server's own words. Every one of these is a sentence the visitor
         needs: "This user isn't accepting new messages", "This user only accepts
         new messages from their followers", "You cannot message this user". */
      const err = (await convoRes.json().catch(() => ({}))) as { error?: string };
      return { ok: false, reason: err.error ?? GENERIC_FAILURE_MESSAGE };
    }
    const convo = (await convoRes.json().catch(() => ({}))) as { id?: string };
    if (!convo.id) return { ok: false, reason: GENERIC_FAILURE_MESSAGE };
    return { ok: true, id: convo.id };
  }

  /** One message. Ok when the server ACCEPTED it. */
  async function post(convoId: string, body: string, idempotencyKey: string): Promise<Outcome> {
    const msgRes = await authFetch(`/api/conversations/${encodeURIComponent(convoId)}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ body }),
    });
    if (msgRes.status === 401) return { ok: false, reason: SIGNIN_MESSAGE };
    if (!msgRes.ok) {
      // 403 here is a block that landed between the two calls, and 400 is an
      // empty or rejected body. Both are the server's to word.
      const err = (await msgRes.json().catch(() => ({}))) as { error?: string };
      return { ok: false, reason: err.error ?? GENERIC_FAILURE_MESSAGE };
    }
    return { ok: true };
  }

  /* The Idempotency-Key the message route honours (idempotencyMiddleware in
     api-server routes/conversations.ts).

     The key of every body that FAILED, by recipient and body, so a tap-again on
     the same text (or the same staged piece) to the same person is the same
     send as far as the server is concerned. If the first try actually landed
     and only the answer was lost, the server replays its stored answer instead
     of posting the message twice. A body's entry goes when that body lands. */
  const failedKeys = new Map<string, string>();
  const keyFor = (recipientId: string, body: string) => `${recipientId}\n${body}`;

  /** Disables (or re-enables) one composer's box and button. Re-queried every
   *  time: a repaint replaces both nodes, so a held reference goes stale. */
  function lock(kind: Composer, on: boolean): void {
    const sels = kind === "deal" ? [MSG_INPUT_SELECTOR, MSG_SEND_SELECTOR] : [RAIL_INPUT_SELECTOR, RAIL_SEND_SELECTOR];
    for (const sel of sels) {
      const el = win.document.querySelector(sel) as (HTMLInputElement | HTMLButtonElement) | null;
      if (el) el.disabled = on;
    }
  }

  function submit(kind: Composer): void {
    const w = win as MessagingWindow;
    const found = resolve(kind);
    if (!found) return;
    const { input, threadId, recipientId, handle = null } = found;

    const note = input.value.trim();
    /* The pieces staged for THIS composer's person, in staging order. The rail
       and a thread pane share one staging area and can be open at once, aimed
       at two people; a piece picked for one must never leave through the
       other's Send. */
    const staged = handle ? w.__gbzMsgAttachFor?.(handle) ?? [] : [];
    // Blake returns silently on empty and so do we: an error toast for pressing
    // send on an empty box with nothing staged is noise, not information.
    if (!note && !staged.length) return;

    if (!recipientId) {
      // 🔴 REFUSE OUT LOUD. A handle with no id, or a thread key we cannot
      // parse, cannot be messaged — and a button that appears to work and does
      // nothing is the defect being fixed here.
      notify(UNKNOWN_RECIPIENT_MESSAGE);
      return;
    }

    if (busy.has(recipientId)) return;
    busy.add(recipientId);
    const writes = threadWrites(win);
    writes.inFlight++;
    writes.epoch++;

    /* One message per staged piece, in staging order, then the note (the
       header: the app's protocol). Each with its own key, a failed body's key
       reused so the retry is the same send to the server. */
    const outgoing: Outgoing[] = staged.map((item) => ({ body: sharedListingBody(item.lid), key: "", item }));
    if (note) outgoing.push({ body: note, key: "", item: null });
    for (const o of outgoing) o.key = failedKeys.get(keyFor(recipientId, o.body)) ?? newAttemptId();
    const wasFocused = win.document.activeElement === input;

    /* 🔴 THE SEND SHOWS AT ONCE, BEFORE ANY REQUEST RETURNS. The requests run
       back to back (find-or-create, each message, the thread re-read) and until
       now nothing on screen moved until the last one finished — so people tapped
       send again, and the repeats were dropped by the guard above without a
       word. Now: a "Sending…" bubble per body goes into the thread's own msgs
       (so Blake's renderer paints it and any repaint in between keeps it), the
       box clears, and the box and button are disabled until the send settles.
       A shared piece's bubble carries its card and reads "Shared a listing" as
       its line (the row line reads it), never the token.

       The bubbles are not fabricated rows that outlive the request: on success
       the re-read below replaces the whole msgs array with the server's, which
       is what carries the real timestamps and the masked bodies; a body that
       did not go is taken out again, and the note goes back into the box. */
    const msgs = threadId ? w.__gbzThreadMsgs?.(threadId) ?? null : null;
    const pendings: PendingAwareMsg[] = outgoing.map((o) => ({
      from: "me",
      text: o.item ? SHARED_LISTING_FALLBACK : o.body,
      when: SENDING_LABEL,
      at: new Date().toISOString(),
      pending: true,
      ...(o.item ? { item: o.item } : {}),
    }));
    if (msgs) msgs.push(...pendings);
    if (kind === "deal") w.__gbzDealsJustSent?.();
    input.value = ""; // first: a repaint paints back what a composer holds (app.js msgDrafts, card 1219598861441931)
    repaint(); // also what clears the box: it replaces the input with a fresh one
    lock(kind, true);

    void (async () => {
      /* How many of `outgoing` the server accepted, front to back: the send
         stops at the first body it refuses, so everything before `landed` is
         on the server and everything from it on is not. */
      let landed = 0;
      let reason: string | null = null;
      try {
        /* The conversation id the thread's own events response already carried.
           Using it skips a whole round trip (and one of the two hits each message
           cost on the write limiter). It is absent until the thread has been
           opened and until the pair has spoken, and then find-or-create stays
           the way in. Read HERE, not behind an await: with the id known the first
           post() is called before this function yields, so the message is on the
           wire before any repaint or answer, as develop's send() had it and as
           dealThreadSendingState pins ("all before the network answers"). */
        const known = (threadId ? w.__gbzThreadConvoId?.(threadId) : null) ?? null;
        const convo: { ok: true; id: string } | { ok: false; reason: string } = known
          ? { ok: true, id: known }
          : await createConversation(recipientId);
        if (!convo.ok) reason = convo.reason;
        else {
          for (const o of outgoing) {
            const r = await post(convo.id, o.body, o.key);
            if (!r.ok) {
              reason = r.reason;
              break;
            }
            landed++;
            failedKeys.delete(keyFor(recipientId, o.body));
          }
        }
      } catch {
        reason = reason ?? GENERIC_FAILURE_MESSAGE;
      }
      const allLanded = landed === outgoing.length;
      const noteLanded = !note || allLanded;

      /* What went leaves the staging area (by listing id, this person's only);
         what did not stays staged for the retry, its key kept. */
      const landedLids = outgoing.slice(0, landed).map((o) => o.item?.lid).filter((lid): lid is string => !!lid);
      if (handle && landedLids.length) w.__gbzMsgAttachDrop?.(handle, landedLids);
      for (const o of outgoing.slice(landed)) failedKeys.set(keyFor(recipientId, o.body), o.key);

      /* The bubbles: what landed loses its pending mark (and has no server
         timestamp yet, so it says so plainly rather than leave "Sending…" up if
         the re-read below cannot run); what did not go comes out. */
      for (let i = 0; i < pendings.length; i++) {
        if (i < landed) {
          delete pendings[i].pending;
          pendings[i].when = JUST_SENT_LABEL;
        } else if (msgs) {
          const at = msgs.indexOf(pendings[i]);
          if (at >= 0) msgs.splice(at, 1);
        }
      }

      try {
        if (!allLanded) {
          /* Every piece went and only the note did not: mobile's own sentence
             (ShareListingModal.tsx), since the recipient is looking at the card.
             Anything else: the server's words for the body that failed. */
          const noteOnly = staged.length > 0 && landed === staged.length;
          notify(noteOnly ? NOTE_NOT_SENT_MESSAGE : reason ?? GENERIC_FAILURE_MESSAGE);
        }

        if (landed > 0) {
          /* Re-read rather than hand-building the rows. Hand-building is what the
             fabricator did, and it is how the rendered thread starts drifting
             from the stored one: a made-up timestamp, no id, and nothing to catch
             a body the server normalized, truncated, or masked — and it DOES
             mask, via screenMessage on the way in.

             Best-effort: the messages are already stored, so a failed read costs a
             repaint, not the messages.

             🪤 THE SCROLL FLAG IS SET AFTER THE RE-READ, right before the last paint.
             The re-read repaints on its own (the bridge calls render()) and
             repaint() paints again after it, and wire() clears the flag on every
             paint — so a flag set earlier is spent on the paint that is about to
             be replaced, and the pane lands back at the top with the new message
             off screen, which is exactly what people reported.

             🔴 A FIRST MESSAGE HAS NO THREAD TO RE-READ. Someone the visitor had
             no conversation or offer with when the page loaded has no row in the
             list, and the rail paints only rows, so the message went, the box
             cleared, and the rail went on saying "No messages yet" until a
             reload (found in real use, 2026-10-15). The list is read again (the merge-in-place
             re-read the offer actions use), which now carries the pair, and the
             new row is read like any other. */
          let readId = threadId;
          if (!readId && kind === "rail" && handle) {
            await refreshDeals(win, authFetch, myUserId).catch(() => null);
            readId = w.__gbzThreadIdForHandle?.(handle) ?? null;
          }
          const reread = readId
            ? await installDealThread(win, authFetch, readId, myUserId).catch(() => false)
            : false;
          if (kind === "deal") w.__gbzDealsJustSent?.();
          repaint();

          /* His deal composer shows no toast and the rail patches the bubble in
             silently, because in both cases the message APPEARING is the feedback.
             That only holds when the re-read landed. When it did not — a thread we
             have no key for, or a failed events fetch — nothing appears, and silence
             would read exactly like the dead button this replaces. A partial send
             has already been spoken for above. */
          if (!reread && allLanded) notify(SENT_MESSAGE);
        } else {
          // Nothing sent: the bubbles are gone, and what was typed comes back.
          repaint();
        }
      } finally {
        busy.delete(recipientId);
        writes.inFlight--;
        writes.epoch++;
        lock(kind, false);
        const box = win.document.querySelector(
          kind === "deal" ? MSG_INPUT_SELECTOR : RAIL_INPUT_SELECTOR,
        ) as HTMLInputElement | null;
        if (box) {
          /* Only if the visitor is still looking at the same conversation: if
             they opened another thread while this was out, the failed text must
             not appear in a different person's box, one tap from being sent to
             them. */
          if (!noteLanded && !box.value && resolve(kind)?.threadId === threadId) box.value = note;
          if (wasFocused) box.focus({ preventScroll: true });
        }
      }
    })();
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    if (target.closest(MSG_SEND_SELECTOR)) submit("deal");
    else if (target.closest(RAIL_SEND_SELECTOR)) submit("rail");
  };

  const onKeydown = (event: Event): void => {
    const ke = event as KeyboardEvent;
    /* Not a repeat: focus can land in this box while Enter is still held (an opener's deferred focus, or a
       failed send's refocus), and the key's auto-repeat would send its text with no Send pressed
       (collection-piece review M-1). */
    if (ke.key !== "Enter" || ke.repeat) return;
    const el = event.target as Element | null;
    /* ONLY the rail's input. #msg-in's Enter synthesises a click on #msg-send,
       which onClick already sees; handling it here too would send twice. */
    if (!el?.matches?.(RAIL_INPUT_SELECTOR)) return;
    submit("rail");
  };

  /* Capture on the document, after the guard, and not stopping propagation —
     the guard kills both fabricators and stays the fail-safe. The keydown guard
     is registered the same way and uses stopPropagation too, so ours still runs
     on the rail's Enter. */
  win.document.addEventListener("click", onClick, true);
  win.document.addEventListener("keydown", onKeydown, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    win.document.removeEventListener("keydown", onKeydown, true);
  };
}
